<template>
    <div class="ppv2-space-y-4">
        <InfoMessageBox
            class="ppv2-mt-2"
            :title="$t('messageBox.billingPolicy.start.title')"
            color="info"
            :customIcon="customIconStart"
        />

        <div class="ppv2-flex ppv2-flex-col ppv2-space-y-4">
            <div class="ppv2-relative ppv2-p-4 ppv2-cursor-pointer">
                <div class="ppv2-absolute ppv2-top-2 ppv2-right-2">
                    <BaseSwitch
                        v-model="formData.is_auto_invoice_active"
                        required
                        :disabled="false"
                        :error="hasError ? 'Invalid' : ''"
                    />
                </div>
                <div>
                    <div class="ppv2-text-base ppv2-text-gray-800 poppinsMedium">
                        <BaseLabel :label="$t('billingPolicy.fields.activateAutoBilling.label')" />
                    </div>
                    <div class="ppv2-flex ppv2-items-center ppv2-space-x-2 ppv2-mt-1 ppv2-text-xs ppv2-text-gray-500">
                        <BaseParagraphe> {{ $t('billingPolicy.fields.activateAutoBilling.description') }} </BaseParagraphe>
                    </div>
                </div>

                <BaseCheckbox
                    v-model="formData.is_invoice_provisoire_active"
                    :label="$t('billingPolicy.fields.activateAutoBilling.options.draftInvoices.label')"
                    :description="$t('billingPolicy.fields.activateAutoBilling.options.draftInvoices.description')"
                    class="ppv2-mt-6"
                    :disabled="false"
                />

                <BaseSelect
                    v-model="formData.numbering_format"
                    :label="$t('billingPolicy.fields.numberingFormat.label')"
                    class="ppv2-mt-6"
                    :tooltipMsg="$t('billingPolicy.fields.numberingFormat.infoMessage')"
                    :options="numberingFormatOptions"
                    required
                />

                <BaseInput
                    v-model="formData.invoice_start_number"
                    :label="$t('billingPolicy.fields.startingNumber.label')"
                    class="ppv2-mt-6"
                    placeholder="1"
                    :tooltipMsg="$t('billingPolicy.fields.startingNumber.infoMessage')"
                >
                    <template #leftIcon>
                        <svg
                            class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10 3L8 21M16 3L14 21M4 9H20M4 15H20"
                                stroke="#021832"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </template>
                </BaseInput>

                <BaseInput
                    v-model="formData.invoice_delay"
                    :label="$t('billingPolicy.fields.paymentDeadline.label')"
                    class="ppv2-mt-6"
                    placeholder="Ex.30"
                    :tooltipMsg="$t('billingPolicy.fields.paymentDeadline.infoMessage')"
                    required
                >
                    <template #leftIcon>
                        <svg
                            class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700"
                            viewBox="0 0 24 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M8 2.5V6.5M16 2.5V6.5M21 14.5V6.5C21 5.96957 20.7893 5.46086 20.4142 5.08579C20.0391 4.71071 19.5304 4.5 19 4.5H5C4.46957 4.5 3.96086 4.71071 3.58579 5.08579C3.21071 5.46086 3 5.96957 3 6.5V20.5C3 21.0304 3.21071 21.5391 3.58579 21.9142C3.96086 22.2893 4.46957 22.5 5 22.5H13M3 10.5H21M16 20.5L18 22.5L22 18.5"
                                stroke="#021832"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </template>
                </BaseInput>
            </div>
        </div>
    </div>
</template>

<script>
import InfoMessageBox from '../../../Shared/InfoMessageBox.vue'
import BaseParagraphe from '../../../Shared/BaseParagraphe.vue'
import BaseRadioGroup from '../../../Shared/BaseRadioGroup.vue'
import BaseCheckbox from '../../../Shared/BaseCheckbox.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseLabel from '../../../Shared/BaseLabel.vue'
import BaseInput from '../../../Shared/BaseInput.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'

const fieldsSettings = [
    'is_auto_invoice_active',
    'is_invoice_provisoire_active',
    'invoice_start_number',
    'invoice_delay',
    'numbering_format',
]

export default {
    name: 'BillingPolicy',
    components: {
        InfoMessageBox,
        BaseParagraphe,
        BaseRadioGroup,
        BaseCheckbox,
        BaseSwitch,
        BaseLabel,
        BaseInput,
        BaseSelect,
    },
    data() {
        return {
            customIconStart: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 5L5 19M9 6.5C9 7.88071 7.88071 9 6.5 9C5.11929 9 4 7.88071 4 6.5C4 5.11929 5.11929 4 6.5 4C7.88071 4 9 5.11929 9 6.5ZM20 17.5C20 18.8807 18.8807 20 17.5 20C16.1193 20 15 18.8807 15 17.5C15 16.1193 16.1193 15 17.5 15C18.8807 15 20 16.1193 20 17.5Z" stroke="#021832" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>`,
            customIconLink: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 3H21M21 3V9M21 3L10 14M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>`,
            isActive: false,
            hasError: false,
            isInitializing: true,
            formData: {
                is_auto_invoice_active: false,
                is_invoice_provisoire_active: false,
                numbering_format: 'PLAY-{YYYY}{MM}{NNN}',
                invoice_start_number: null,
                invoice_delay: null,
            },
        }
    },
    computed: {
        establishment() {
            return this.$store.getters['establishment/establishment']
        },
        appSetting() {
            return this.$store.getters['establishment/appSetting']
        },
        invoiceSequences() {
            return
        },
        numberingFormatOptions() {
            const invoiceSequences = this.$store.getters['establishment/invoiceSequences']

            if (Array.isArray(invoiceSequences) && invoiceSequences.length > 0) {
                return invoiceSequences.map(({ pattern, ...rest }) => ({
                    ...rest,
                    value: pattern,
                }))
            }

            return []
        },
        numberingFormatOptionsOld() {
            return [
                { label: 'PLAY-{ANNÉE}{MOIS}{NUMÉRO}', value: 'PLAY-{YYYY}{MM}{NNN}' },
                { label: 'PLAY-{NUMÉRO}{ANNÉE}{MOIS}', value: 'PLAY-{NNN}{YYYY}{MM}' },
                { label: 'PLAY-{MOIS}{NUMÉRO}{ANNÉE}', value: 'PLAY-{MM}{NNN}{YYYY}' },
                { label: 'Numéro séquentiel', value: '{NNN}' },
                { label: 'Année-Numéro séquentiel', value: '{YYYY}-{NNN}' },
                { label: 'Date-Numéro séquentiel', value: '{YYYY}{MM}{DD}-{NNN}' },
            ]
        },
    },
    watch: {
        formData: {
            deep: true,
            handler(newVal) {
                if (this.isInitializing) return
                this.$emit('update', newVal)
                const updatedAppSettings = Object.entries(newVal)
                    .filter(([key]) => fieldsSettings.includes(key))
                    .map(([key, value]) => ({ key, value }))
                this.$store.commit('establishment/ADD_APP_SETTING', updatedAppSettings)
            },
        },
    },
    methods: {
        autoFillFromWebsiteSetting() {
            if (!this.appSetting || !Array.isArray(this.appSetting)) return

            this.appSetting.forEach((item) => {
                if (fieldsSettings.includes(item.key)) {
                    const currentValue = this.formData[item.key]

                    if (typeof currentValue === 'boolean') {
                        this.formData[item.key] = item.value == 1 || item.value === true || item.value === '1'
                    } else if (typeof currentValue === 'number') {
                        this.formData[item.key] = Number(item.value)
                    } else {
                        this.formData[item.key] = item.value
                    }
                }
            })
        },
    },
    created() {
        this.autoFillFromWebsiteSetting()
        this.$nextTick(() => {
            this.isInitializing = false
        })
    },
}
</script>

<style scoped></style>
